import React, { useEffect, useState } from 'react';

/**
 * Looping four-stage workflow preview for the Funeral Professional
 * program page. Replaces the static `fd-portal-glass.png` with a
 * self-contained React/SVG animation that shows the real product
 * sequence: intake notes → AI drafting → branded PDF → download.
 *
 * Rendered entirely with CSS transitions + a simple setInterval state
 * machine — no <video>, no external assets — so it works offline,
 * loads instantly, and stays in sync with branding changes. The
 * sequence is decorative so the component is marked aria-hidden.
 *
 * Stage durations tuned so each screen reads as a deliberate step
 * (not a blur) and the full loop is 10 seconds — short enough that
 * a prospect watches at least one full cycle while scanning the hero.
 */

const STAGES = [
  { id: 'intake',   label: 'Director pastes rough intake',     duration: 2600 },
  { id: 'drafting', label: 'AI drafts a respectful obituary',   duration: 2600 },
  { id: 'pdf',      label: 'Your logo + firm name applied',     duration: 2600 },
  { id: 'download', label: 'Branded PDF ready for the family',  duration: 2200 },
];

// Character-by-character typing simulation for the intake stage.
// We don't run a real timer per keystroke — instead we compute how
// many characters should be visible at each animation frame given
// the elapsed time in the stage. That keeps the effect smooth and
// avoids 100+ setState calls per stage.
const INTAKE_TEXT =
  'Margaret W., 82, Asheville NC\n' +
  'Born 5/14/1943. Died 3/2/2026.\n' +
  'Husband Tom (64 yrs). 3 kids.\n' +
  '5th grade teacher 32 yrs. Choir.\n' +
  'First Baptist. Loved her peppermints.';

function useIntakeProgress(active, durationMs) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!active) {
      setProgress(0);
      return undefined;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (durationMs * 0.85));
      setProgress(t);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [active, durationMs]);
  return progress;
}

const IntakeScreen = ({ active, duration }) => {
  const progress = useIntakeProgress(active, duration);
  const chars = Math.floor(INTAKE_TEXT.length * progress);
  const shown = INTAKE_TEXT.substring(0, chars);
  return (
    <div className="fd-wf-screen fd-wf-screen-intake">
      <div className="fd-wf-window-bar" aria-hidden="true">
        <span /><span /><span />
        <span className="fd-wf-window-label">Intake notes</span>
      </div>
      <div className="fd-wf-textarea">
        <span className="fd-wf-textarea-text">{shown}</span>
        <span className="fd-wf-caret" aria-hidden="true" />
      </div>
      <div className="fd-wf-textarea-hint">
        Paste from your family-meeting notes — any order works
      </div>
    </div>
  );
};

const DraftingScreen = ({ active }) => (
  <div className="fd-wf-screen fd-wf-screen-draft">
    <div className="fd-wf-window-bar" aria-hidden="true">
      <span /><span /><span />
      <span className="fd-wf-window-label">ObitAuthor is writing</span>
    </div>
    <div className="fd-wf-draft-head">
      <span className="fd-wf-spark">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v3M12 18v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M3 12h3M18 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </span>
      Drafting obituary…
    </div>
    <div className={`fd-wf-lines ${active ? 'is-running' : ''}`}>
      <span className="fd-wf-line" style={{ width: '92%', animationDelay: '.05s' }} />
      <span className="fd-wf-line" style={{ width: '88%', animationDelay: '.25s' }} />
      <span className="fd-wf-line" style={{ width: '94%', animationDelay: '.45s' }} />
      <span className="fd-wf-line" style={{ width: '71%', animationDelay: '.65s' }} />
      <span className="fd-wf-line" style={{ width: '90%', animationDelay: '.85s' }} />
      <span className="fd-wf-line" style={{ width: '62%', animationDelay: '1.05s' }} />
    </div>
  </div>
);

const PdfScreen = ({ active, stage }) => (
  <div className={`fd-wf-screen fd-wf-screen-pdf ${stage === 'download' ? 'is-download' : ''}`}>
    <div className="fd-wf-window-bar" aria-hidden="true">
      <span /><span /><span />
      <span className="fd-wf-window-label">Preview — Obituary.pdf</span>
    </div>
    <div className="fd-wf-paper" aria-hidden="true">
      <div className="fd-wf-paper-mast">
        <div className="fd-wf-paper-logo">
          {/* Minimal "dove" mark representing the firm's uploaded logo */}
          <svg viewBox="0 0 40 40" width="26" height="26" fill="currentColor" aria-hidden="true">
            <path d="M7 22c4-1 7-4 9-8 1 3 0 7-2 10 4-1 7-3 9-6-1 4-4 8-9 9-4 1-7 0-8-1l1-4z" />
          </svg>
        </div>
        <div className="fd-wf-paper-firm">BLUE RIDGE MEMORIAL CHAPEL</div>
      </div>
      <div className="fd-wf-paper-oh">In Loving Memory of</div>
      <div className="fd-wf-paper-name">Eleanor Margaret Whitfield</div>
      <div className="fd-wf-paper-dates">May 14, 1938 — March 2, 2026</div>
      <div className="fd-wf-paper-body">
        <span /><span /><span style={{ width: '70%' }} />
        <span /><span /><span style={{ width: '62%' }} />
        <span /><span style={{ width: '48%' }} />
      </div>
    </div>
    <div className={`fd-wf-download-chip ${stage === 'download' && active ? 'is-shown' : ''}`}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
           stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12" />
        <path d="M7 10l5 5 5-5" />
        <path d="M4 21h16" />
      </svg>
      Downloaded
    </div>
  </div>
);

const FdWorkflowPreview = () => {
  const [stageIdx, setStageIdx] = useState(0);
  useEffect(() => {
    const stage = STAGES[stageIdx];
    const t = setTimeout(() => setStageIdx((i) => (i + 1) % STAGES.length), stage.duration);
    return () => clearTimeout(t);
  }, [stageIdx]);

  const activeStage = STAGES[stageIdx];

  return (
    <div className="fd-wf-wrap" aria-hidden="true">
      <div className="fd-wf-frame">
        <div className="fd-wf-track" data-stage={activeStage.id}>
          <div className={`fd-wf-slot ${activeStage.id === 'intake' ? 'is-active' : ''}`}>
            <IntakeScreen active={activeStage.id === 'intake'} duration={STAGES[0].duration} />
          </div>
          <div className={`fd-wf-slot ${activeStage.id === 'drafting' ? 'is-active' : ''}`}>
            <DraftingScreen active={activeStage.id === 'drafting'} />
          </div>
          <div className={`fd-wf-slot ${activeStage.id === 'pdf' || activeStage.id === 'download' ? 'is-active' : ''}`}>
            <PdfScreen active={activeStage.id === 'pdf' || activeStage.id === 'download'} stage={activeStage.id} />
          </div>
        </div>
      </div>

      <ol className="fd-wf-steps">
        {STAGES.map((s, i) => {
          // "pdf" and "download" share the same visual slot; collapse them
          // in the stepper so the dots don't flash twice at the end.
          if (s.id === 'download') return null;
          const active = activeStage.id === s.id || (s.id === 'pdf' && activeStage.id === 'download');
          const done = i < stageIdx && !(s.id === 'pdf' && activeStage.id === 'download');
          return (
            <li key={s.id} className={`fd-wf-step ${active ? 'is-active' : ''} ${done ? 'is-done' : ''}`}>
              <span className="fd-wf-dot" />
              <span className="fd-wf-step-label">{s.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default FdWorkflowPreview;
